// @owners { team: patient-health }
import { TYPOGRAPHY } from '@alto/design-library-tokens';
// eslint-disable-next-line import/no-deprecated
import { Body, CenteredActivityIndicator, Column, InlineAlert, MdPadding } from '@alto/design-system';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import { useProduct } from '~shared/queries';

const Markdown = styled(ReactMarkdown)`
  > p {
    font-size: ${TYPOGRAPHY.TEXT.BODY.MD.rem};

    > strong {
      font-size: ${TYPOGRAPHY.TEXT.HEADER.SM.rem};
    }
  }
`;

type Props = {
  readonly productID: number;
};

export const LearnMore = ({ productID }: Props) => {
  const { isError, isProductPending, product } = useProduct({ productID });

  if (isError && !product) {
    return (
      <InlineAlert type="error">
        <Column
          center
          gap="SM"
        >
          <Body>Something went wrong loading product information.</Body>
          <Body>Please try again or message support if the problem persists.</Body>
        </Column>
      </InlineAlert>
    );
  }

  if (isProductPending && !product) {
    return (
      <Column>
        <MdPadding>
          <CenteredActivityIndicator accessibilityHint="loading product info" />
        </MdPadding>
        <Body center>Loading product info...</Body>
      </Column>
    );
  }

  if (product?.monograph) {
    return (
      <Column>
        <Markdown source={product.monograph} />
      </Column>
    );
  }

  /**
   * There are a handful of prescriptions that have a denormalized monograph set on them, but their
   * corresponding products don't have the monograph set. Punt on handling this edge case for now.
   */
  return (
    <Column>
      <Body>We don&#39;t seem to have any additional information on file for this product.</Body>
    </Column>
  );
};
